import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Space, Tag, Typography } from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import { getCaseAssist, requestSuggestion } from '@/api/caseAssist';
import type { AssistAvailability, AssistKind, AssistRef, AssistState, CaseSuggestion } from '@/api/caseAssist';
import { normalizeApiError } from '@/api/errors';
import { formatAbsolute } from '@/lib/time';

const { Text } = Typography;

const KINDS: Array<{ kind: AssistKind; ask: string; title: string }> = [
  { kind: 'CASE_SUMMARY', ask: 'Summarise this case', title: 'Case summary' },
  { kind: 'FOLLOW_UP_ANALYSIS', ask: 'Analyse questionnaire answers', title: 'Questionnaire follow-up analysis' },
  { kind: 'TRIAGE', ask: 'Suggest where to look first', title: 'Triage suggestions' },
];

const STATE_TEXT: Record<AssistAvailability, string> = {
  READY: 'A model is available. Each request uses part of this hub\'s budget.',
  NOT_CONFIGURED: 'No model is connected. Suggestions are built from the case record, without a model, and use no budget.',
  KILLED: 'Your hub has switched the model off. Suggestions are built from the case record, without a model, and use no budget.',
  OVER_BUDGET: 'This hub\'s budget for assistance is used up this month. Suggestions are built from the case record, without a model, and use no budget.',
};

const DEGRADE_TEXT: Record<string, string> = {
  CITATION_VIOLATION: 'A model answered, but its answer failed the citation and wording checks, so it was not used.',
  PROVIDER_UNAVAILABLE: 'The model could not be reached.',
  ENVELOPE_VIOLATION: 'The model\'s settings were refused by the gateway.',
};

const ATTENTION_COLOR: Record<string, string> = { HIGH: 'error', MEDIUM: 'warning', LOW: 'default' };
const ATTENTION_LABEL: Record<string, string> = { HIGH: 'Look first', MEDIUM: 'Look next', LOW: 'Later' };

/** Where a reference opens on this page. The caller owns the tab and the highlight. */
export type OpenRef = (ref: AssistRef) => void;

function builtByText(s: CaseSuggestion): string {
  return s.builtBy === 'MODEL'
    ? `Drafted by a model${s.modelId ? ` (${s.modelId})` : ''}`
    : 'Built from the case record, without a model';
}

function SuggestionCard({ title, suggestion, onOpen, onRefresh, busy, canRequest }: {
  title: string;
  suggestion: CaseSuggestion;
  onOpen: OpenRef;
  onRefresh: () => void;
  busy: boolean;
  canRequest: boolean;
}) {
  return (
    <section className="ol-card" style={{ padding: '12px 14px' }} data-testid="suggestion" data-kind={suggestion.kind} data-built-by={suggestion.builtBy} aria-label={title}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <Tag bordered={false} color="purple" data-testid="suggestion-label">Suggestion</Tag>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{title}</span>
        <Text style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)' }} data-testid="suggestion-built-by">{builtByText(suggestion)}</Text>
      </div>
      {suggestion.erased ? (
        <p style={{ margin: '8px 0 0', fontSize: 12.5 }} data-testid="suggestion-erased">
          <Tag bordered={false} color="warning">Erased</Tag> It rests on evidence that has been erased, or that you cannot see, so its text is not shown.
        </p>
      ) : (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {suggestion.claims.map((c, i) => (
            <li key={i} data-testid="suggestion-claim">
              {c.attention && <Tag bordered={false} color={ATTENTION_COLOR[c.attention]}>{ATTENTION_LABEL[c.attention]}</Tag>}
              {c.text}{' '}
              {c.refs.map((r) => (
                r.type === 'WORK_ITEM' ? (
                  <Link key={r.type + r.id} to={`/workbench?item=${encodeURIComponent(r.id)}`} data-testid="suggestion-ref" style={{ fontSize: 11.5, marginRight: 6 }}>{r.label}</Link>
                ) : (
                  <Button key={r.type + r.id} type="link" size="small" data-testid="suggestion-ref" data-ref-type={r.type} style={{ padding: 0, height: 'auto', fontSize: 11.5, marginRight: 6 }} onClick={() => onOpen(r)}>
                    {r.label}
                  </Button>
                )
              ))}
            </li>
          ))}
        </ul>
      )}
      {suggestion.limits && <p style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>{suggestion.limits}</p>}
      {suggestion.degradeReason && DEGRADE_TEXT[suggestion.degradeReason] && (
        <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>{DEGRADE_TEXT[suggestion.degradeReason]}</p>
      )}
      <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--ol-text-tertiary)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span>Asked by {suggestion.requestedBy} · {formatAbsolute(suggestion.createdAt)}{suggestion.recorded ? ' · recorded result, nothing spent' : ''}</span>
        {suggestion.stale && !suggestion.erased && (
          <span data-testid="suggestion-stale" style={{ color: 'var(--ol-text-secondary)' }}>
            The case has changed since this was built.{' '}
            {canRequest && <Button size="small" loading={busy} onClick={onRefresh} data-testid="suggestion-refresh">Refresh</Button>}
          </span>
        )}
      </div>
    </section>
  );
}

/**
 * Assistance on a case (OL-31, ADR-011). Everything here is a suggestion: it reads, and nothing it shows has a control that changes
 * the case. The page's load reads what is on record; only a click asks for anything, and if this panel's read fails the case page
 * carries on without it.
 */
export function CaseAssistPanel({ runId, onOpen, refreshKey }: { runId: string; onOpen: OpenRef; refreshKey?: number }) {
  const [state, setState] = useState<AssistState | null>(null);
  const [suggestions, setSuggestions] = useState<Partial<Record<AssistKind, CaseSuggestion>>>({});
  const [busy, setBusy] = useState<AssistKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const read = useCallback(async () => {
    const view = await getCaseAssist(runId);
    setState(view.state);
    setSuggestions(Object.fromEntries(view.suggestions.map((s) => [s.kind, s])));
  }, [runId]);

  useEffect(() => {
    read().catch(() => setState(null));
  }, [read, refreshKey]);

  const ask = async (kind: AssistKind) => {
    setBusy(kind);
    setError(null);
    try {
      const made = await requestSuggestion(runId, kind);
      // Read the rest again, since the state may have moved, and keep what this click was told about itself.
      await read().catch(() => undefined);
      setSuggestions((was) => ({ ...was, [kind]: made }));
    } catch (e) {
      setError(normalizeApiError(e).message);
    } finally {
      setBusy(null);
    }
  };

  if (!state) return null;
  const canAsk = state.canRequest && state.hasSubject;
  return (
    <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="case-assist">
      <div className="ol-card" style={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 13, display: 'inline-flex', gap: 6, alignItems: 'center' }}><BulbOutlined aria-hidden /> Assistance</span>
          {canAsk && (
            <Space size={6} wrap>
              {KINDS.map(({ kind, ask: label }) => (
                <Button key={kind} size="small" loading={busy === kind} disabled={busy !== null && busy !== kind} onClick={() => void ask(kind)} data-testid={`assist-${kind}`}>
                  {label}
                </Button>
              ))}
            </Space>
          )}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>
          Suggestions only. They change nothing on this case: decisions stay with the normal controls.{' '}
          <span data-testid="assist-state" data-availability={state.availability}>{STATE_TEXT[state.availability]}</span>
          {state.availability === 'KILLED' && state.killedBy && (
            <span data-testid="assist-killed-by"> Switched off by {state.killedBy}{state.killedAt ? ` on ${formatAbsolute(state.killedAt)}` : ''}{state.killReason ? `: ${state.killReason}` : ''}.</span>
          )}
          {!state.hasSubject && ' This run has no subject bound, so there is nothing to build a suggestion from.'}
        </p>
      </div>
      {error && <Alert type="warning" showIcon message={error} closable onClose={() => setError(null)} data-testid="assist-error" />}
      {KINDS.map(({ kind, title }) => {
        const s = suggestions[kind];
        return s ? <SuggestionCard key={kind} title={title} suggestion={s} onOpen={onOpen} busy={busy === kind} canRequest={canAsk} onRefresh={() => void ask(kind)} /> : null;
      })}
    </div>
  );
}
